import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listProjects } from '@mkcore/portal/schema';
import { Badge, Card, EmptyState, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { healthTone } from '@/features/portal/projects';

/** The projects of the client (CO-0661): status, health, the latest shared weekly status and what waits for it. */
export const Route = createFileRoute('/portal/_client/projects/')({
  component: Projects,
});

function Projects() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['portal', 'projects'], queryFn: () => call(listProjects, {}) });
  if (!q.data) return <Skeleton className="h-32" />;
  return (
    <>
      <h1 className="text-2xl font-bold">{t('portal.modules.projects')}</h1>
      {!q.data.items.length ? (
        <EmptyState title={t('portal.projects.empty')} />
      ) : (
        <ul className="grid gap-2">
          {q.data.items.map((p) => (
            <li key={p.id}>
              <Card className="grid gap-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link to="/portal/projects/$id" params={{ id: p.id }} className="font-bold hover:underline">
                    {p.name}
                  </Link>
                  <span className="flex flex-wrap gap-1">
                    {p.health && <Badge tone={healthTone[p.health]}>{t(`portal.projects.health.${p.health}`)}</Badge>}
                    {p.status !== 'active' && <Badge tone="neutral">{t(`portal.projects.status.${p.status}`, { defaultValue: p.status })}</Badge>}
                  </span>
                </div>
                {(p.milestonesToDecide > 0 || p.openClientTasks > 0) && (
                  <p className="text-sm font-bold">
                    {[p.milestonesToDecide > 0 && t('portal.projects.toDecide', { count: p.milestonesToDecide }), p.openClientTasks > 0 && t('portal.projects.openTasks', { count: p.openClientTasks })]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
                {p.latestUpdate && (
                  <p className="line-clamp-3 whitespace-pre-wrap text-sm text-fg-2">
                    <span className="font-bold">{t('portal.projects.weekOf', { date: formatDate(p.latestUpdate.week) })}: </span>
                    {p.latestUpdate.summary}
                  </p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
